/**
 * Main suggestion engine coordinating all sources.
 * Generates search suggestions based on input data.
 *
 * Rules:
 * - Pure function, no side effects
 * - Priority: History → Categories → Products
 * - Uses full history (not pre-filtered)
 * - All filtering happens internally
 * - Returns plain string[] for UI consumption
 */

import { SearchSuggestion, SuggestionInput, SuggestionOptions } from './types';
import {
  extractHistorySuggestions,
  extractCategorySuggestions,
  extractProductSuggestions,
} from './suggestionSources';
import { deduplicateSuggestions, limitSuggestions } from './suggestionUtils';

const DEFAULT_OPTIONS: Required<SuggestionOptions> = {
  maxSuggestions: 7,
  minQueryLength: 2,
  includeStartsWith: true,
};

/**
 * Generate search suggestions from all available sources.
 *
 * Algorithm:
 * 1. Validate query meets minimum length
 * 2. Extract suggestions from each source (in priority order)
 * 3. Combine while preserving priority order
 * 4. Deduplicate across all sources (case-insensitive)
 * 5. Limit to maxSuggestions
 *
 * @param input Input data (query, history, categories, products)
 * @param options Configuration options
 * @returns Array of suggestion strings (empty if no matches)
 */
export function generateSuggestions(
  input: SuggestionInput,
  options?: SuggestionOptions
): SearchSuggestion[] {
  const mergedOptions = { ...DEFAULT_OPTIONS, ...options };
  const { query } = input;

  // Validate query
  if (!query || query.trim().length < mergedOptions.minQueryLength) {
    return [];
  }

  const trimmedQuery = query.trim();

  // Extract suggestions from each source (in priority order)
  const allSuggestions: SearchSuggestion[] = [];

  // 1. History suggestions (highest priority)
  const historySuggestions = extractHistorySuggestions(
    input.historyItems,
    trimmedQuery,
    mergedOptions
  );
  allSuggestions.push(...historySuggestions);

  // 2. Category suggestions (medium priority)
  const categorySuggestions = extractCategorySuggestions(
    input.categories,
    trimmedQuery,
    mergedOptions
  );
  allSuggestions.push(...categorySuggestions);

  // 3. Product suggestions (lowest priority)
  const productSuggestions = extractProductSuggestions(
    input.products,
    trimmedQuery,
    mergedOptions
  );
  allSuggestions.push(...productSuggestions);

  // If no suggestions found, return empty array
  if (allSuggestions.length === 0) {
    return [];
  }

  // Deduplicate (preserves first occurrence, maintaining priority order)
  const deduplicated = deduplicateSuggestions(allSuggestions);

  // Limit to maximum number of suggestions
  const limited = limitSuggestions(deduplicated, mergedOptions.maxSuggestions);

  return limited;
}

/**
 * Export default options for external use.
 */
export { DEFAULT_OPTIONS };